'use client';

import { useState, useEffect } from 'react';

interface DocumentItem {
  id: number;
  docKey: string;
  labelId: string;
  labelEn: string;
  format: string;
  maxSize: string;
  deadline?: string;
  required: boolean;
}

interface DocumentChecklistProps {
  documents: DocumentItem[];
  universityAbbrev: string;
  pathway: string;
  lang: string;
  /** Source URL for data attribution */
  sourceUrl?: string;
}

const STORAGE_KEY_PREFIX = 'doccheck-';

export function DocumentChecklist({ documents, universityAbbrev, pathway, lang, sourceUrl }: DocumentChecklistProps) {
  const isId = lang === 'id';
  const storageKey = `${STORAGE_KEY_PREFIX}${universityAbbrev}-${pathway}`;

  const [checked, setChecked] = useState<Set<string>>(new Set());

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setChecked(new Set(JSON.parse(stored)));
      }
    } catch { /* ignore */ }
  }, [storageKey]);

  // Save to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...checked]));
    } catch { /* ignore */ }
  }, [checked, storageKey]);

  function toggle(docKey: string) {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(docKey)) next.delete(docKey);
      else next.add(docKey);
      return next;
    });
  }

  const total = documents.length;
  const done = documents.filter(d => checked.has(d.docKey)).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-card p-4 md:p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-base font-bold text-text-primary">
          {isId ? `Checklist Dokumen ${universityAbbrev} — ${pathway.toUpperCase()}` : `Document Checklist ${universityAbbrev} — ${pathway.toUpperCase()}`}
        </h3>
        <span className="text-xs font-medium text-text-secondary">
          {done}/{total} ({pct}%)
        </span>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-2 w-full rounded-full bg-surface-hover overflow-hidden">
        <div
          className="h-full rounded-full bg-semantic-success transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Document list */}
      <ul className="mt-4 space-y-2">
        {documents.map(doc => {
          const isChecked = checked.has(doc.docKey);
          return (
            <li key={doc.docKey}>
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                isChecked
                  ? 'panel-success border'
                  : 'border-border-subtle bg-surface-root hover:bg-surface-hover'
              }`}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(doc.docKey)}
                  className="mt-0.5 h-4 w-4 rounded border-border-subtle text-semantic-success focus:ring-semantic-success"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${isChecked ? 'text-semantic-success line-through' : 'text-text-primary'}`}>
                      {isId ? doc.labelId : doc.labelEn}
                    </span>
                    {doc.required ? (
                      <span className="badge-error inline-block rounded-full px-1.5 py-0.5 text-caption font-semibold">
                        {isId ? 'Wajib' : 'Required'}
                      </span>
                    ) : (
                      <span className="badge-neutral inline-block rounded-full px-1.5 py-0.5 text-caption font-medium">
                        {isId ? 'Opsional' : 'Optional'}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-text-tertiary">
                    {doc.format} · {isId ? 'Maks' : 'Max'} {doc.maxSize}
                    {doc.deadline && ` · ${isId ? 'Batas' : 'Deadline'}: ${doc.deadline}`}
                  </p>
                </div>
              </label>
            </li>
          );
        })}
      </ul>

      {pct === 100 && (
        <div className="panel-success mt-4 rounded-lg border p-3 text-center text-sm font-semibold text-semantic-success">
          {isId ? 'Semua dokumen sudah lengkap!' : 'All documents are ready!'}
        </div>
      )}

      {sourceUrl && (
        <p className="mt-4 text-xs text-text-tertiary">
          {isId ? 'Sumber:' : 'Source:'}{' '}
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-primary hover:underline"
          >
            {new URL(sourceUrl).hostname}
          </a>
        </p>
      )}
    </div>
  );
}
